import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ReportPeriodDto } from '@anessa/shared-models';
import { I18nPipe } from '../../../shared/i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../../shared/i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../../shared/i18n/i18n-scoped.service';
import { ReportService } from '../../../features/report-creation/services/report.service';

export interface ReportMetadataDialogData {
  mode: 'create' | 'edit';
  reportId?: string;
  initialName: string;
  initialPeriod: ReportPeriodDto;
}

export interface ReportMetadataDialogResult {
  name: string;
  reportPeriod: ReportPeriodDto;
}

@Component({
  selector: 'app-report-metadata-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    I18nPipe,
    I18nScopeReadyDirective,
  ],
  templateUrl: './report-metadata-dialog.html',
  styleUrl: './report-metadata-dialog.scss',
  providers: [
    provideI18nScope('ui/report/report-metadata-dialog'),
    I18nScopedService,
  ],
})
export class ReportMetadataDialog {
  private readonly dialogRef = inject(MatDialogRef<ReportMetadataDialog>);
  private readonly reportService = inject(ReportService);

  readonly data = inject(MAT_DIALOG_DATA) as ReportMetadataDialogData;

  name = this.data.initialName;
  periodStartDate = this.parseDate(this.data.initialPeriod.startDate);
  periodEndDate = this.parseDate(this.data.initialPeriod.endDate);

  hasDuplicateName(): boolean {
    const candidate = this.name.trim().toLowerCase();
    if (!candidate) return false;

    return this.reportService.reportList().some((report) => {
      if (this.data.reportId && report.id === this.data.reportId) return false;
      return report.name.trim().toLowerCase() === candidate;
    });
  }

  isPeriodSelectionMissing(): boolean {
    return !this.periodStartDate || !this.periodEndDate;
  }

  hasInvalidPeriodRange(): boolean {
    if (!this.periodStartDate || !this.periodEndDate) return false;
    return this.periodStartDate.getTime() > this.periodEndDate.getTime();
  }

  canApply(): boolean {
    return (
      !!this.name.trim() &&
      !this.hasDuplicateName() &&
      !this.isPeriodSelectionMissing() &&
      !this.hasInvalidPeriodRange()
    );
  }

  apply(): void {
    if (!this.canApply()) return;
    if (!this.periodStartDate || !this.periodEndDate) return;

    this.dialogRef.close({
      name: this.name.trim(),
      reportPeriod: {
        startDate: this.toDateOnlyString(this.periodStartDate),
        endDate: this.toDateOnlyString(this.periodEndDate),
      },
    } satisfies ReportMetadataDialogResult);
  }

  cancel(): void {
    this.dialogRef.close();
  }


  private parseDate(value: string | null): Date | null {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toDateOnlyString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

